import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { BillingSummary, PackageCard, AssociationNotice } from './PortalCards';
import { fullName, money, polishDate, lessonTypeLabel } from './clientPortalSelectors';
import { portalAttendanceOutcome } from '../../../LambdaFunctions/shared/portalAttendance';

export default function ClientPortalSection({ section, data, snapshot, selectedStudentId, renderLesson }) {
  const { t: tr, i18n } = useTranslation();
  const ledgerLabels = {
    YEAR_TRANSFER_OUT: tr('billing:yearScope.out'),
    YEAR_TRANSFER_IN: tr('billing:yearScope.in'),
    YEAR_TRANSFER_REVERSAL: tr('billing:yearScope.reversal'),
    PAYMENT: tr("client:payment_17"),
    OPENING_BALANCE: tr("client:openingBalance_18"),
    MONTHLY_CHARGE: tr("client:monthlyCharge_19"),
    CORRECTION: tr("common:legacyOption6"),
    REFUND: tr("common:legacyOption7"),
  };
  const fallback = (snapshot.students || []).length === 1 ? data.unassociated : { packages: [], courses: [], materials: [] };
  const lessonTypes = snapshot.lessonTypes || [];
  if (section === 'schedule') return <section className="client-portal__card">{data.schedule.length ? data.schedule.map(renderLesson) : <p>{tr("client:noUpcomingLessons_6")}</p>}</section>;
  if (section === 'packages') {
    return (
      <section className="client-portal__card">
        {data.unknownAssociations.packages && <AssociationNotice />}
        {data.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        {fallback.packages.length > 0 && (
        <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
          {fallback.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        </div>
        )}
        {!data.packages.length && !data.unknownAssociations.packages && <p>{tr("client:noPackages_7")}</p>}
      </section>
    );
  }
  if (section === 'billing') {
    return (
      <>
        <BillingSummary ledger={data.ledger} client={snapshot.client || {}} />
        {data.ledger && (
        <section className="client-portal__card"><h2>{tr("client:payerTransactions_21")}</h2>
          <p>{tr("client:payments_22")} {money(data.ledger.paymentsTotal, i18n.language)}</p><p>{tr("client:charges_23")} {money(data.ledger.chargesTotal, i18n.language)}</p>
          {!(data.ledger.entries || []).length && <p>{tr("client:noTransactions_24")}</p>}
          {(data.ledger.entries || []).map(entry => (
            <article className="client-portal__detail-row" key={entry.id}>
              <div>{polishDate(entry.date, false, i18n.language)}</div><div>{ledgerLabels[entry.type] || tr("manage:extraSettlementTransaction_91")}</div>
              {entry.schoolYearStartYear && <div>{tr('billing:yearScope.year')}: {entry.schoolYearStartYear}/{String(entry.schoolYearStartYear + 1).slice(-2)}</div>}
              {entry.description && <div>{entry.description}</div>}<strong>{money(entry.amount, i18n.language)}</strong>
            </article>
          ))}
        </section>
        )}
        <section className="client-portal__card"><h2>{tr("client:invoices_25")}</h2>{!data.invoices.length && <p>{tr("client:noInvoices_26")}</p>}
          {data.invoices.map(invoice => (
            <article className="client-portal__detail-row" key={invoice.id}><div>{invoice.invoice || invoice.id}</div>
              {/^https?:\/\//i.test(invoice.invoiceUrl || '') && <a href={invoice.invoiceUrl} target="_blank" rel="noopener noreferrer">{tr("client:openInvoice_27")}</a>}
              {invoice.date && <div>{polishDate(invoice.date, false, i18n.language)}</div>}{invoice.amount != null && <div>{money(invoice.amount, i18n.language)}</div>}
            </article>
          ))}
        </section>
      </>
    );
  }
  if (section === 'summaries') {
    const students = (snapshot.students || []).filter(student => !selectedStudentId || student.id === selectedStudentId);
    return (
      <section className="client-portal__card client-portal__lesson-summaries">
        {!students.length && <p>{tr("client:noAssignedStudents_0")}</p>}
        {students.map(student => (
          <div key={student.id}><h2>{fullName(student)}</h2>
            {!data.summaries.some(item => item.studentId === student.id) && <p>{tr("client:noCompletedLessons_28")}</p>}
            {data.summaries.filter(item => item.studentId === student.id).map(item => (
              <article key={`${item.lessonId}-${item.studentId}`} className="client-portal__lesson-summary">
                <p className="text-muted">{polishDate(item.date, false, i18n.language)}{item.startTime ? `, ${item.startTime}` : ''}</p>
                {item.status === 'cancelled' && <p>{tr("manage:lessonCancelled_54")}</p>}
                {item.status === 'noSummary' && <p>{tr("manage:noSummaryHasBeenAdded_88")}</p>}
                {item.status === 'summarized' && <p>{item.lessonSummary}</p>}
              </article>
            ))}
          </div>
        ))}
      </section>
    );
  }
  if (section === 'attendance') {
    return (
      <section className="client-portal__card">
        {data.attendance ? <p>{tr("client:attendance_29")} {data.attendance.present}/{data.attendance.total} ({data.attendance.rate}%)</p> : <p>{tr("client:noAttendanceData_30")}</p>}
        {data.attendanceLessons.map(item => (
          <article className="client-portal__detail-row" key={`${item.id}-${item.studentId}`}>
            <div>{polishDate(item.date, false, i18n.language)} {item.startTime}</div><div>{item.studentName}</div><div>{tr(`client:attendanceOutcome.${portalAttendanceOutcome(item)}`)}</div>
          </article>
        ))}
      </section>
    );
  }
  if (section === 'history') {
    return (
      <>
        <section className="client-portal__card"><h2>{tr("client:lessons_33")}</h2>{!data.historyLessons.length && <p>{tr("client:noLessonHistory_34")}</p>}
          {data.historyLessons.map(item => (
            <article key={`${item.id}-${item.studentId}`} className="client-portal__detail-row">
              <div>{polishDate(item.date, false, i18n.language)} {item.startTime}</div><div>{item.studentName}</div><div>{lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
            </article>
          ))}
        </section>
        <section className="client-portal__card"><h2>{tr("client:courses_35")}</h2>{data.unknownAssociations.courses && <AssociationNotice />}
          {!data.courses.length && !data.unknownAssociations.courses && <p>{tr("client:noCourseHistory_36")}</p>}
          {fallback.courses.length > 0 && (
          <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
            {fallback.courses.map(item => (
              <article className="client-portal__detail-row" key={item.id}>
                <div>{lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
                {item.fromDate && <div>{tr("manage:from_295")} {polishDate(item.fromDate, false, i18n.language)}</div>}{item.tillDate && <div>{tr("manage:to_296")} {polishDate(item.tillDate, false, i18n.language)}</div>}
              </article>
            ))}
          </div>
          )}
          {data.courses.map(item => (
            <article className="client-portal__detail-row" key={item.id}><div>{item.name || lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
              {item.fromDate && <div>{tr("manage:from_295")} {polishDate(item.fromDate, false, i18n.language)}</div>}{item.tillDate && <div>{tr("manage:to_296")} {polishDate(item.tillDate, false, i18n.language)}</div>}
            </article>
          ))}
        </section>
      </>
    );
  }
  return (
    <section className="client-portal__card">
      {data.unknownAssociations.materials && <AssociationNotice />}
      {!data.materials.length && !data.unknownAssociations.materials && <p>{tr("client:noMaterials_37")}</p>}
      {fallback.materials.length > 0 && (
      <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
        {fallback.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h3>{item.title}</h3>{item.description && <p>{item.description}</p>}</article>)}
      </div>
      )}
      {data.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h2>{item.title}</h2>{item.description && <p>{item.description}</p>}</article>)}
    </section>
  );
}
ClientPortalSection.propTypes = {
  section: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  snapshot: PropTypes.object.isRequired,
  selectedStudentId: PropTypes.string,
  renderLesson: PropTypes.func.isRequired,
};
ClientPortalSection.defaultProps = { selectedStudentId: null };
